import { Component, Input, OnChanges, OnDestroy, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';

import { BuergschaftenComponent } from '../buergschaften.component';
import { BuergschaftsAntragTypes } from '../../../../../model.module/bgsch-antrag-type.model';
import { AuthService } from '../../../../../services.module/auth.service';
import { Permission } from '../../../../../model.module/constant/rights.enum';
import { RouteNames } from '../../../../../route.name';
import { ClientDownloadService } from '../../../../../services.module/client-download.service';
import { MessageModel } from '../../../../../model.module/modals.model';
import { MessageObservable } from '../../../../../services.module/message.observable';
import { Buergschein, BuergscheinLight } from '../../../../../../generated/api/kreditportalBackendAPI.schemas';
import { BuergscheineService } from '../../../../../providers.module/buergscheine.service';
import { PipeFunctions } from 'src/app/pipes.module/_pipe.functions';

@Component({
    selector: 'single-buergschaft',
    templateUrl: './bgsch-single.component.html',
    styleUrls: ['../../../../commonless/buergschaften.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BuergschaftenSingleComponent implements OnInit, OnChanges, OnDestroy {

    @Input()
    vsnr: string = null;

    @Input()
    vsnrFormatiert: string = null;

    @Input()
    buergscheinId: string = null;

    @Input()
    parent: BuergschaftenComponent;

    buergschein: Buergschein;

    buergscheinLight: BuergscheinLight;

    neighborScheine: string[] = [];

    rueckforderungLocked = false;

    isLoading = signal(false);

    constructor(
        private readonly router: Router,
        private readonly clientDownloadService: ClientDownloadService,
        private readonly messageOberservable: MessageObservable,
        private readonly authService: AuthService,
        private readonly buergscheineService: BuergscheineService
    ) {
    }

    ngOnInit() {
        if (this.vsnrFormatiert) {
            this.loadBuergschein();
        }
    }

    ngOnChanges() {
        if (this.vsnrFormatiert) {
            this.loadBuergschein();
        }
    }

    ngOnDestroy() {
        this.buergscheinLight = null;
        this.buergschein = null;
        this.neighborScheine = [];
    }

    canModifyBs(): boolean {
        return (this.authService.getRole() & Permission.READ_CREATE_BS) === Permission.READ_CREATE_BS
            && this.buergscheinLight.statusanzeigeBuergschaft === 'AUSGESTELLT';
    }

    private loadBuergschein(): void {
        this.isLoading.set(true);
        this.buergschein = null;
        this.buergscheinLight = null;

        forkJoin({
            buergschein: this.buergscheineService.getBuergschein(this.vsnrFormatiert, this.buergscheinId),
            buergscheine: this.buergscheineService.getBuergscheine(this.vsnrFormatiert),
        }).subscribe({
            next: ({ buergschein, buergscheine }) => {
                this.buergschein = buergschein;

                const buergscheinShort = buergscheine.buergscheine.find(
                    currBuergscheinShort => this.buergscheinId === currBuergscheinShort.buergscheinId
                );

                if (buergscheinShort) {
                    this.buergscheinLight = buergscheinShort;
                }

                // FIXME findNeighborsFor
            },
            complete: () => {
                this.isLoading.set(false);
            },
        });
    }

    createBuergscheinWithVorlage(aendern = false): void {
        const alter = aendern ? 1 : 0;
        const rigidVsnr = PipeFunctions.formatVsnrRigid(this.vsnrFormatiert);

        this.router.navigate(
            [RouteNames.KREDIT_PORTAL_BUERGSCHAFTEN, 'create-buergschein'],
            { queryParams: { vsnr: rigidVsnr, bgsch: this.buergscheinId, alter } },
        );
    }

    next() {
        console.debug('VSNR:', this.vsnrFormatiert);
        this.router.navigate(
            [RouteNames.KREDIT_PORTAL_BUERGSCHAFTEN, this.vsnrFormatiert],
            { queryParams: { param: this.neighborScheine[1] } },
        );
    }

    prev() {
        this.router.navigate(
            [RouteNames.KREDIT_PORTAL_BUERGSCHAFTEN, this.vsnrFormatiert],
            { queryParams: { param: this.neighborScheine[0] } },
        );
    }

    goBack() {
        this.router.navigate([RouteNames.KREDIT_PORTAL_BUERGSCHAFTEN]);
    }

    translate(abbr: string): string {
        if (!abbr) {
            return '';
        }

        const formularTupel = BuergschaftsAntragTypes.findByFormularKuerzel(abbr);

        if (formularTupel) {
            return formularTupel.right;
        }

        return 'Sonstiges Formular';
    }

    translateBuergscheinArt(abbr: string): string {
        if (!abbr) {
            return '';
        }

        return BuergschaftsAntragTypes.findByBuergschaftsart(abbr).label;
    }

    downloadBuergschein(original = false): void {
        const vsnr = this.vsnrFormatiert;
        const bsnr = this.buergscheinId;

        this.clientDownloadService.openPdfInNewTab(
            this.buergscheineService.getBuergscheinDokument(vsnr, bsnr, original),
            () => alert('Für die Bürgschaft ist noch kein Dokument verfügbar.')
        );
    }

    rueckforderungBuergschaft(): void {
        const vsnr = this.vsnrFormatiert;
        const bsnr = this.buergscheinId;

        this.rueckforderungLocked = true;

        const localStorageKey = `rueckforderung_${vsnr}_${bsnr}`;
        const timestamp = (new Date()).valueOf();

        const retrievedTimestamp = window.sessionStorage.getItem(localStorageKey);

        if (retrievedTimestamp) {
            const messageModel = new MessageModel(
                'Rückforderung bereits beantragt',
                [
                    `Ein Rückforderungswunsch für Bürgschaft ${bsnr} wurde heute schon einmal ausgelöst, daher
                     ist eine erneute Anforderung erst wieder zu einen späteren Zeitpunkt möglich.`
                ]
            );
            this.messageOberservable.notify(messageModel);

            return;
        }

        window.sessionStorage.setItem(localStorageKey, String(timestamp));

        this.buergscheineService.postBuergscheinRueckforderung(vsnr, bsnr).subscribe({
            next: () => {
                const messageModel = new MessageModel(
                    'Rückforderung erfolgreich beantragt',
                    [
                        `Der Auftraggeber wurde darüber informiert, dass Sie die Bürgschaft ${bsnr} zurückgefordert haben.`,
                        'Bei erfolgter Rückgabe wird die Bürgschaft zeitnah ausgebucht.',
                        'Bei einer Ablehnung werden Sie über die Nachrichtenfunktion des Kreditportals über diese informiert.'
                    ]
                );
                this.messageOberservable.notify(messageModel);
            },
            error: () => {
                this.rueckforderungLocked = false;
            }
        });
    }

}
